import React, { useRef, useState, useEffect } from "react";
import {
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { VideoPlayer } from "./HelpDialogItems";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const FunctionsSwitcherHoriz = ({ functions }) => {
  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  if (!functions || functions.length === 0) {
    return <Typography>No functions available</Typography>;
  }

  const scrollAmount = 300;

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding issues
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      // Initial check
      updateScrollButtons();

      // Add scroll listener
      scrollContainer.addEventListener("scroll", updateScrollButtons);

      // Add resize listener to handle window resizing
      const resizeObserver = new ResizeObserver(updateScrollButtons);
      resizeObserver.observe(scrollContainer);

      return () => {
        scrollContainer.removeEventListener("scroll", updateScrollButtons);
        resizeObserver.disconnect();
      };
    }
  }, [functions]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left:
          direction === "left"
            ? scrollLeft - scrollAmount
            : scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Scroll Controls - Only visible on desktop and when scrolling is possible */}
      {isDesktop && canScrollLeft && (
        <IconButton
          onClick={() => handleScroll("left")}
          sx={{
            position: "absolute",
            left: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              bgcolor: theme.palette.background.default,
            },
          }}
        >
          <ArrowBackIosNewIcon fontSize="medium" />
        </IconButton>
      )}

      {isDesktop && canScrollRight && (
        <IconButton
          onClick={() => handleScroll("right")}
          sx={{
            position: "absolute",
            right: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              bgcolor: theme.palette.background.default,
            },
          }}
        >
          <ArrowForwardIosIcon fontSize="medium" />
        </IconButton>
      )}

      {/* Horizontal Scrollable Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollbarWidth: "none", // Firefox
          "&::-webkit-scrollbar": {
            display: "none", // Chrome, Safari, Opera
          },
          "-ms-overflow-style": "none", // IE and Edge
          gap: 6,
          pb: 2,
          pt: 1,
          px: 1,
        }}
      >
        {functions.map((func, index) => (
          <Box
            component={motion.div}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            key={index}
            sx={{
              minWidth: isMobile ? "65vw" : isDesktop ? "350px" : "280px",
              width: isMobile ? "65vw" : "570px",
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
              },
              height: "100%",
              flexShrink: 0,
            }}
          >
            {/* Video Section - First */}
            <Box
              sx={{
                width: "100%",
                borderRadius: "12px 12px 0 0",
                overflow: "hidden",
              }}
            >
              <VideoPlayer
                key={func.videoUrl}
                videoUrl={func.videoUrl}
                poster={func.poster}
                autoPlay={false}
                gridDisplay={true}
                horizDisplay={true}
              />
            </Box>

            {/* Title and Description Section - Below */}
            <Box
              sx={{
                p: 4,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                gutterBottom
                color="text.primary"
              >
                {func.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "1rem",
                }}
              >
                {func.subtitle || func.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Scroll indicator */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 2,
          gap: 0.5,
        }}
      >
        {functions.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[700]
                  : theme.palette.grey[300],
              opacity: 0.7,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default FunctionsSwitcherHoriz;
